import { useState } from 'react'
import { supabase } from '../api/supabase'
import { useToast } from '../hooks/useToast'

export interface InvoiceToAddressSide {
  address_lines: string[]
  city: string | null
  postcode: string | null
  country: string | null
}

export interface InvoiceToMismatch {
  // Z58.6: every call now carries a real status -- 'match' still means the dialog renders (staff
  // must click "Confirm & Continue" even when everything lines up), it's just styled green
  // instead of demanding a reconciliation choice. Never silently skip this dialog on 'match'.
  status: 'match' | 'partial' | 'mismatch'
  invoiceToCustomerId: string
  invoiceToCustomerName: string
  parentAccNum: string
  ours: InvoiceToAddressSide
  boxtop: InvoiceToAddressSide | null
  scorePct?: number
  note?: string
}

interface Props {
  mismatch: InvoiceToMismatch
  onResolved: () => void
  onCancel: () => void
}

export function StatusBadge({ status }: { status: InvoiceToMismatch['status'] }) {
  const config = {
    match: { icon: '✓', label: 'Verified', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    partial: { icon: '⚠', label: 'Partial match', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
    mismatch: { icon: '✗', label: 'Mismatch', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  }[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${config.className}`}>
      <span>{config.icon}</span>{config.label}
    </span>
  )
}

function AddressPanel({ title, address }: { title: string; address: InvoiceToAddressSide | null }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-1 text-sm">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</p>
      {address ? (
        <>
          {address.address_lines.map((l, i) => <p key={i} className="text-gray-900 dark:text-white">{l}</p>)}
          <p className="text-gray-900 dark:text-white">{[address.city, address.postcode].filter(Boolean).join(', ') || '—'}</p>
          <p className="text-gray-500 dark:text-gray-400">{address.country || '—'}</p>
        </>
      ) : (
        <p className="text-red-600 dark:text-red-400">Not found on BoxTop — this account number doesn't resolve to a real Contact.</p>
      )}
    </div>
  )
}

// Z58.5 Stage 1: shown when sync-to-boxtrax returns result:'invoice_to_address_mismatch' (or
// 'invoice_to_not_bound', handled by the caller as a plain error toast instead -- this dialog is
// only for the "bound but inconsistent" case). Every job syncs its InvoiceTo customer as the
// BoxTop parent Contact every other party ultimately associates with, so this is verified before
// any Shipper/Consignee/Notify matching runs, not per-party.
export default function InvoiceToVerifyDialog({ mismatch, onResolved, onCancel }: Props) {
  const [saving, setSaving] = useState<'boxtop' | 'ours' | 'edit' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editLines, setEditLines] = useState(mismatch.ours.address_lines.join('\n'))
  const [editCity, setEditCity] = useState(mismatch.ours.city || '')
  const [editPostcode, setEditPostcode] = useState(mismatch.ours.postcode || '')
  const { toast } = useToast()

  // Best-effort -- the audit table migration may not have been run yet (see Z57: a page must
  // never hard-depend on a migration that hasn't landed). A logging failure never blocks the
  // actual resolution.
  const logVerification = async (action: 'use_boxtop' | 'edit_both', after: InvoiceToAddressSide) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      await supabase.from('customer_address_verifications').insert({
        customer_id: mismatch.invoiceToCustomerId,
        verified_by: sessionData?.session?.user?.email || null,
        action_taken: action,
        before_state: { ours: mismatch.ours, boxtop: mismatch.boxtop },
        after_state: after,
      })
    } catch (e) {
      console.warn('[InvoiceToVerifyDialog] Failed to log verification (non-blocking):', e)
    }
  }

  const writeOurAddress = async (address: InvoiceToAddressSide) => {
    const { error: err } = await supabase
      .from('customer_addresses')
      .update({
        address_lines: address.address_lines,
        city: address.city,
        postcode: address.postcode,
        country: address.country,
        address_manually_verified: true,
      })
      .eq('customer_id', mismatch.invoiceToCustomerId)
      .eq('address_type', 'operations')
    if (err) throw err
  }

  const useBoxtop = async () => {
    if (!mismatch.boxtop) return
    setSaving('boxtop'); setError(null)
    try {
      await writeOurAddress(mismatch.boxtop)
      await logVerification('use_boxtop', mismatch.boxtop)
      toast(`Updated ${mismatch.invoiceToCustomerName}'s address from BoxTop`, 'success')
      onResolved()
    } catch (err: any) {
      setError(err?.message || 'Failed to update our address')
    } finally {
      setSaving(null)
    }
  }

  const saveEdited = async () => {
    setSaving('edit'); setError(null)
    try {
      const edited: InvoiceToAddressSide = {
        address_lines: editLines.split('\n').map(l => l.trim()).filter(Boolean),
        city: editCity.trim() || null,
        postcode: editPostcode.trim() || null,
        country: mismatch.ours.country,
      }
      await writeOurAddress(edited)
      await logVerification('edit_both', edited)
      toast(`Saved edited address for ${mismatch.invoiceToCustomerName} (ours only; BoxTop is not changed)`, 'success')
      onResolved()
    } catch (err: any) {
      setError(err?.message || 'Failed to save edited address')
    } finally {
      setSaving(null)
    }
  }

  const busy = saving !== null
  const matched = mismatch.status === 'match'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Step 1 of 2: Verify InvoiceTo / Customer</p>
          <div className="flex items-center gap-2 mt-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Verify Customer / Principal Address</h2>
            <StatusBadge status={mismatch.status} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {mismatch.invoiceToCustomerName} (BoxTop account {mismatch.parentAccNum}) —{' '}
            {matched
              ? "our stored address matches BoxTop's record for this account. Confirm to continue to Shipper/Consignee/Notify."
              : "our stored address doesn't match what BoxTop has on file for this account. Resolve before syncing."}
          </p>
        </div>

        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
          {mismatch.note && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm">{mismatch.note}</div>}

          {!editing ? (
            <div className="grid grid-cols-2 gap-3">
              <AddressPanel title="Our records" address={mismatch.ours} />
              <AddressPanel title="BoxTop" address={mismatch.boxtop} />
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs text-gray-600 dark:text-gray-400">Address lines (one per line)</span>
                <textarea
                  value={editLines}
                  onChange={e => setEditLines(e.target.value)}
                  rows={3}
                  className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-gray-600 dark:text-gray-400">City</span>
                  <input value={editCity} onChange={e => setEditCity(e.target.value)} className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Postcode</span>
                  <input value={editPostcode} onChange={e => setEditPostcode(e.target.value)} className="mt-1 w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1.5" />
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2">
          {matched ? (
            <>
              <button onClick={onResolved} className="w-full px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium">
                Confirm &amp; Continue →
              </button>
              <button onClick={onCancel} className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:underline">
                Cancel sync
              </button>
            </>
          ) : !editing ? (
            <>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Resolve Mismatch:</p>
              <div className="flex gap-2">
                <button onClick={useBoxtop} disabled={busy || !mismatch.boxtop} className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50">
                  {saving === 'boxtop' ? 'Saving…' : 'Use BoxTop → update ours'}
                </button>
                <button onClick={() => setEditing(true)} disabled={busy} className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                  Edit ours
                </button>
              </div>
              <button onClick={onCancel} disabled={busy} className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-50">
                Cancel sync
              </button>
            </>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} disabled={busy} className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Back
              </button>
              <button onClick={saveEdited} disabled={busy} className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50">
                {saving === 'edit' ? 'Saving…' : 'Save our address'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
